<script>
  import './styles/view/CertificateIssue.css';
  import Navigation from './components/layout/Navigation.svelte';
  import { API_BASE_URL } from './config.js';
  import { onMount, onDestroy } from 'svelte';

  export let user;

  // 5분 동안 조작이 없으면 자동 새로고침
  let inactivityTimer;
  const INACTIVITY_TIMEOUT = 5 * 60 * 1000; // 5분

  function resetInactivityTimer() {
    if (inactivityTimer) {
      clearTimeout(inactivityTimer);
    }
    inactivityTimer = setTimeout(() => {
      // 새로고침 후 certificate-issue 페이지로 돌아오도록 저장
      sessionStorage.setItem('returnPage', 'certificate-issue');
      location.reload();
    }, INACTIVITY_TIMEOUT);
  }

  onMount(() => {
    // 초기 타이머 시작
    resetInactivityTimer();

    // 사용자 조작 감지 이벤트
    window.addEventListener('mousemove', resetInactivityTimer);
    window.addEventListener('mousedown', resetInactivityTimer);
    window.addEventListener('keydown', resetInactivityTimer);
    window.addEventListener('scroll', resetInactivityTimer);
    window.addEventListener('touchstart', resetInactivityTimer);
  });

  onDestroy(() => {
    if (inactivityTimer) {
      clearTimeout(inactivityTimer);
    }
    // 이벤트 리스너 제거
    window.removeEventListener('mousemove', resetInactivityTimer);
    window.removeEventListener('mousedown', resetInactivityTimer);
    window.removeEventListener('keydown', resetInactivityTimer);
    window.removeEventListener('scroll', resetInactivityTimer);
    window.removeEventListener('touchstart', resetInactivityTimer);
  });
  export let setPage = () => {};
  export let isAdmin = false;

  let activeTab = 'apply'; // apply: 신청, history: 신청내역, admin: 관리 (관리자용)
  let certificateType = 'appointment'; // appointment: 위촉, dismissal: 해촉

  // 신청 폼 데이터
  let formData = {
    name: '',
    residentNumber: '',
    address: '',
    department: '',
    position: '',
    startDate: '',
    endDate: '',
    purpose: '',
    contact: ''
  };

  // 신청 내역
  let certificates = [];
  let adminCertificates = [];
  let isLoading = false;
  let message = '';
  let messageType = 'info'; // info, success, error

  // 관리자 필터
  let adminFilter = {
    type: '',
    status: '',
    search: ''
  };

  // 모달 상태
  let showDetailModal = false;
  let showSuccessModal = false;
  let showEditCommentModal = false;
  let selectedCertificate = null;
  let adminComment = '';
  let editingComment = '';
  let isCommentUpdating = false;

  // 관리자 수정 모드
  let isEditMode = false;
  let editFormData = {
    name: '',
    residentNumber: '',
    address: '',
    department: '',
    position: '',
    startDate: '',
    endDate: '',
    purpose: '',
    contact: ''
  };
  let isUpdating = false;

  // PDF 업로드 관련
  let pdfFileInput;
  let selectedPdfFile = null;
  let isUploading = false;

  // 상태 텍스트 매핑
  const statusText = {
    0: '신청',
    1: '승인',
    2: '반려',
    3: '재발급'
  };

  const statusClass = {
    0: 'status-pending',
    1: 'status-approved',
    2: 'status-rejected',
    3: 'status-reissued'
  };

  const typeText = {
    'appointment': '위촉증명서',
    'dismissal': '해촉증명서'
  };

  // 사용자 정보로 폼 초기화
  $: if (user && formData.name === '') {
    formData.name = user.name || '';
    formData.contact = user.phone || '';
  }

  // 탭 변경 시 데이터 로드
  $: if (activeTab === 'history' && user) {
    loadUserCertificates();
  }

  $: if (activeTab === 'admin' && isAdmin) {
    loadAdminCertificates();
  }

  function setActiveTab(tab) {
    activeTab = tab;
    message = '';
  }

  async function loadUserCertificates() {
    if (!user) return;

    isLoading = true;
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/api/certificates`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        certificates = data.certificates || [];
      } else if (response.status === 401 || response.status === 403) {
        // 토큰 만료, 무효화, 또는 유효하지 않은 토큰 시 로그인 페이지로 이동
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        alert('세션이 만료되었습니다. 다시 로그인해주세요.');
        window.location.href = '/?t=' + Date.now() + '#login';
        return;
      } else {
        throw new Error('목록 조회 실패');
      }
    } catch (error) {
      console.error('증명서 목록 조회 오류:', error);
      // 네트워크 에러 등의 경우 에러 메시지 표시 (토큰은 유지)
      message = '서버 연결에 실패했습니다. 잠시 후 다시 시도해주세요.';
      messageType = 'error';
    } finally {
      isLoading = false;
    }
  }

  async function loadAdminCertificates() {
    if (!isAdmin) return;

    isLoading = true;
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams();
      if (adminFilter.type) params.append('type', adminFilter.type);
      if (adminFilter.status !== '') params.append('status', adminFilter.status);
      if (adminFilter.search) params.append('search', adminFilter.search);

      const response = await fetch(`${API_BASE_URL}/api/certificates/admin?${params}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        adminCertificates = data.certificates || [];
      } else if (response.status === 401 || response.status === 403) {
        // 토큰 만료, 무효화, 또는 유효하지 않은 토큰 시 로그인 페이지로 이동
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        alert('세션이 만료되었습니다. 다시 로그인해주세요.');
        window.location.href = '/?t=' + Date.now() + '#login';
        return;
      } else {
        throw new Error('목록 조회 실패');
      }
    } catch (error) {
      console.error('관리자 증명서 목록 조회 오류:', error);
      // 네트워크 에러 등의 경우 에러 메시지 표시 (토큰은 유지)
      message = '서버 연결에 실패했습니다. 잠시 후 다시 시도해주세요.';
      messageType = 'error';
    } finally {
      isLoading = false;
    }
  }

  async function handleSubmit() {
    if (!user) {
      message = '로그인이 필요합니다.';
      messageType = 'error';
      return;
    }

    // 필수 필드 검증
    if (!formData.name || !formData.startDate) {
      message = '필수 항목(성명, 시작일)을 입력해주세요.';
      messageType = 'error';
      return;
    }

    isLoading = true;
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/api/certificates`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          type: certificateType,
          ...formData
        })
      });

      const result = await response.json();

      if (response.ok) {
        // 성공 모달 표시
        showSuccessModal = true;
        // 폼 초기화
        formData = {
          name: user.name || '',
          residentNumber: '',
          address: '',
          department: '',
          position: '',
          startDate: '',
          endDate: '',
          purpose: '',
          contact: user.phone || ''
        };
      } else {
        throw new Error(result.message || '신청 실패');
      }
    } catch (error) {
      console.error('증명서 신청 오류:', error);
      message = error.message || '신청에 실패했습니다.';
      messageType = 'error';
    } finally {
      isLoading = false;
    }
  }

  function openDetailModal(cert) {
    selectedCertificate = cert;
    adminComment = cert.admin_comment || '';
    showDetailModal = true;
  }

  function closeDetailModal() {
    showDetailModal = false;
    selectedCertificate = null;
    adminComment = '';
    editingComment = '';
    isCommentUpdating = false;
    isEditMode = false;
    isUpdating = false;
  }

  // 수정 모드 전환
  function toggleEditMode() {
    if (!isEditMode && selectedCertificate) {
      // 수정 모드 진입 시 현재 값으로 폼 초기화
      editFormData = {
        name: selectedCertificate.name || '',
        residentNumber: selectedCertificate.resident_number || '',
        address: selectedCertificate.address || '',
        department: selectedCertificate.department || '',
        position: selectedCertificate.position || '',
        startDate: selectedCertificate.start_date ? selectedCertificate.start_date.split('T')[0] : '',
        endDate: selectedCertificate.end_date ? selectedCertificate.end_date.split('T')[0] : '',
        purpose: selectedCertificate.purpose || '',
        contact: selectedCertificate.contact || ''
      };
    }
    isEditMode = !isEditMode;
  }

  // 관리자 증명서 정보 수정
  async function handleAdminUpdate() {
    if (!selectedCertificate || !editFormData.name) {
      message = '성명은 필수 항목입니다.';
      messageType = 'error';
      return;
    }

    isUpdating = true;
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/api/certificates/${selectedCertificate.id}/admin`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(editFormData)
      });

      const result = await response.json();

      if (response.ok) {
        message = result.message;
        messageType = 'success';
        // 선택된 증명서 정보 업데이트
        selectedCertificate = {
          ...selectedCertificate,
          name: editFormData.name,
          resident_number: editFormData.residentNumber,
          address: editFormData.address,
          department: editFormData.department,
          position: editFormData.position,
          start_date: editFormData.startDate,
          end_date: editFormData.endDate,
          purpose: editFormData.purpose,
          contact: editFormData.contact
        };
        isEditMode = false;
        loadAdminCertificates();
      } else {
        throw new Error(result.message || '수정 실패');
      }
    } catch (error) {
      console.error('증명서 수정 오류:', error);
      message = error.message || '수정에 실패했습니다.';
      messageType = 'error';
    } finally {
      isUpdating = false;
    }
  }

  function openEditCommentModal() {
    if (!selectedCertificate) return;
    editingComment = selectedCertificate.admin_comment || '';
    showEditCommentModal = true;
  }

  function closeEditCommentModal() {
    showEditCommentModal = false;
    editingComment = '';
    isCommentUpdating = false;
  }

  async function handleStatusChange(status) {
    if (!selectedCertificate) return;

    // 재발급(status=3)인 경우 확인 알림창 표시
    if (status === 3) {
      if (!confirm('정말로 재발급하시겠습니까?')) {
        return;
      }
    }

    isLoading = true;
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/api/certificates/${selectedCertificate.id}/status`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ status, adminComment })
      });

      const result = await response.json();

      if (response.ok) {
        message = result.message;
        messageType = 'success';
        closeDetailModal();
        loadAdminCertificates();
      } else {
        throw new Error(result.message || '처리 실패');
      }
    } catch (error) {
      console.error('상태 변경 오류:', error);
      message = error.message || '처리에 실패했습니다.';
      messageType = 'error';
    } finally {
      isLoading = false;
    }
  }

  async function handleCommentUpdate() {
    if (!selectedCertificate || !editingComment.trim()) return;

    isCommentUpdating = true;
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/api/certificates/${selectedCertificate.id}/comment`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ adminComment: editingComment.trim() })
      });

      const result = await response.json();

      if (response.ok) {
        message = result.message;
        messageType = 'success';

        // 선택된 증명서 정보 업데이트
        selectedCertificate = result.certificate;
        adminComment = result.certificate.admin_comment;

        closeEditCommentModal();
        loadAdminCertificates();
      } else {
        throw new Error(result.message || '코멘트 수정 실패');
      }
    } catch (error) {
      console.error('코멘트 수정 오류:', error);
      message = error.message || '코멘트 수정에 실패했습니다.';
      messageType = 'error';
    } finally {
      isCommentUpdating = false;
    }
  }

  async function handleDelete(id) {
    if (!confirm('정말 삭제하시겠습니까?')) return;

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/api/certificates/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      const result = await response.json();

      if (response.ok) {
        message = '삭제되었습니다.';
        messageType = 'success';
        loadUserCertificates();
      } else {
        throw new Error(result.message || '삭제 실패');
      }
    } catch (error) {
      console.error('삭제 오류:', error);
      message = error.message || '삭제에 실패했습니다.';
      messageType = 'error';
    }
  }

  function formatDate(dateString) {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('ko-KR');
  }

  function formatDateTime(dateString) {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleString('ko-KR');
  }

  // 승인일로부터 2주 경과 여부 확인
  function isDownloadExpired(cert) {
    if (!cert.approved_at) return false;
    const approvedDate = new Date(cert.approved_at);
    const twoWeeksLater = new Date(approvedDate);
    twoWeeksLater.setDate(twoWeeksLater.getDate() + 14);
    return new Date() > twoWeeksLater;
  }

  async function downloadPDF(id) {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/api/certificates/${id}/pdf`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) {
        const result = await response.json();
        throw new Error(result.message || 'PDF 다운로드 실패');
      }

      // Blob으로 변환 후 다운로드
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;

      // Content-Disposition에서 파일명 추출 또는 기본 파일명 사용
      const contentDisposition = response.headers.get('Content-Disposition');
      let fileName = '증명서.pdf';
      if (contentDisposition) {
        const match = contentDisposition.match(/filename="(.+)"/);
        if (match) {
          fileName = decodeURIComponent(match[1]);
        }
      }

      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      message = 'PDF 다운로드가 완료되었습니다.';
      messageType = 'success';
    } catch (error) {
      console.error('PDF 다운로드 오류:', error);
      message = error.message || 'PDF 다운로드에 실패했습니다.';
      messageType = 'error';
    }
  }

  async function downloadDOCX(id) {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/api/certificates/${id}/docx`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) {
        const result = await response.json();
        throw new Error(result.message || 'DOCX 다운로드 실패');
      }

      // Blob으로 변환 후 다운로드
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;

      // Content-Disposition에서 파일명 추출 또는 기본 파일명 사용
      const contentDisposition = response.headers.get('Content-Disposition');
      let fileName = '증명서.docx';
      if (contentDisposition) {
        const match = contentDisposition.match(/filename="(.+)"/);
        if (match) {
          fileName = decodeURIComponent(match[1]);
        }
      }

      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      message = 'DOCX 다운로드가 완료되었습니다.';
      messageType = 'success';
    } catch (error) {
      console.error('DOCX 다운로드 오류:', error);
      message = error.message || 'DOCX 다운로드에 실패했습니다.';
      messageType = 'error';
    }
  }

  // PDF 파일 선택 핸들러
  function handlePdfFileSelect(event) {
    const file = event.target.files[0];
    if (file) {
      if (file.type !== 'application/pdf') {
        message = 'PDF 파일만 업로드 가능합니다.';
        messageType = 'error';
        event.target.value = '';
        return;
      }
      selectedPdfFile = file;
    }
  }

  // PDF 업로드 핸들러
  async function handlePdfUpload() {
    if (!selectedPdfFile || !selectedCertificate) return;

    isUploading = true;
    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('file', selectedPdfFile);

      const response = await fetch(`${API_BASE_URL}/api/certificates/${selectedCertificate.id}/upload-pdf`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      const result = await response.json();

      if (response.ok) {
        message = 'PDF가 업로드되었습니다.';
        messageType = 'success';
        selectedPdfFile = null;
        if (pdfFileInput) pdfFileInput.value = '';
        // 증명서 정보 업데이트
        selectedCertificate = { ...selectedCertificate, pdf_file_id: result.file.id };
        // 목록 새로고침
        loadAdminCertificates();
      } else {
        throw new Error(result.message || 'PDF 업로드 실패');
      }
    } catch (error) {
      console.error('PDF 업로드 오류:', error);
      message = error.message || 'PDF 업로드에 실패했습니다.';
      messageType = 'error';
    } finally {
      isUploading = false;
    }
  }

  // PDF 삭제 핸들러
  async function handlePdfDelete() {
    if (!selectedCertificate || !selectedCertificate.pdf_file_id) return;
    if (!confirm('업로드된 PDF를 삭제하시겠습니까?')) return;

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/api/certificates/${selectedCertificate.id}/pdf`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const result = await response.json();

      if (response.ok) {
        message = 'PDF가 삭제되었습니다.';
        messageType = 'success';
        selectedCertificate = { ...selectedCertificate, pdf_file_id: null };
        loadAdminCertificates();
      } else {
        throw new Error(result.message || 'PDF 삭제 실패');
      }
    } catch (error) {
      console.error('PDF 삭제 오류:', error);
      message = error.message || 'PDF 삭제에 실패했습니다.';
      messageType = 'error';
    }
  }
</script>

<div class="certificate-container">
  <Navigation
    {user}
    currentPage="certificate-issue"
  />

  <div class="tabs-header">
    <h2 class="page-title">증명서 발급</h2>
    <div class="tabs">
      <button
        class="tab-btn"
        class:active={activeTab === 'apply'}
        on:click={() => setActiveTab('apply')}>
        증명서 신청
      </button>
      <button
        class="tab-btn"
        class:active={activeTab === 'history'}
        on:click={() => setActiveTab('history')}>
        신청 내역
      </button>
      {#if isAdmin}
        <button
          class="tab-btn"
          class:active={activeTab === 'admin'}
          on:click={() => setActiveTab('admin')}>
          관리자
        </button>
      {/if}
    </div>
  </div>

  <div class="content-container">
    {#if message}
      <div class="message {messageType}">{message}</div>
    {/if}

    <div class="tab-content">
      {#if activeTab === 'apply'}
        <!-- 증명서 신청 폼 -->
        <div class="apply-section">
          {#if !user}
            <div class="login-notice">
              <p>증명서를 신청하려면 로그인이 필요합니다.</p>
              <button class="btn-primary" on:click={() => setPage('login')}>로그인</button>
            </div>
          {:else}
            <form on:submit|preventDefault={handleSubmit} class="apply-form">
              <div class="form-row">
                <div class="form-group">
                  <label for="name">성명 <span class="required">*</span></label>
                  <input type="text" id="name" bind:value={formData.name} required />
                </div>
                <div class="form-group">
                  <label for="residentNumber">주민등록번호</label>
                  <input type="text" id="residentNumber" bind:value={formData.residentNumber} placeholder="000000-0000000" />
                </div>
              </div>

              <div class="form-group">
                <label for="address">주소</label>
                <input type="text" id="address" bind:value={formData.address} />
              </div>

              <div class="form-row">
                <div class="form-group">
                  <label for="department">부서</label>
                  <input type="text" id="department" bind:value={formData.department} />
                </div>
                <div class="form-group">
                  <label for="position">직위</label>
                  <input type="text" id="position" bind:value={formData.position} />
                </div>
              </div>

              <div class="form-row">
                <div class="form-group">
                  <label for="startDate">재직기간 시작일 <span class="required">*</span></label>
                  <input type="date" id="startDate" bind:value={formData.startDate} required />
                </div>
                <div class="form-group">
                  <label for="endDate">재직기간 종료일</label>
                  <input type="date" id="endDate" bind:value={formData.endDate} />
                </div>
              </div>

              <div class="form-group">
                <label for="purpose">발급용도</label>
                <input type="text" id="purpose" bind:value={formData.purpose} placeholder="예: 경력증명, 기타" />
              </div>

              <div class="form-group">
                <label for="contact">연락처</label>
                <input type="text" id="contact" bind:value={formData.contact} placeholder="010-0000-0000" />
              </div>

              <div class="type-selector">
                <span class="type-label">증명서 종류 <span class="required">*</span></span>
                <div class="type-options">
                  <label class="radio-label">
                    <input type="radio" bind:group={certificateType} value="appointment" />
                    <span>위촉증명서</span>
                  </label>
                  <label class="radio-label">
                    <input type="radio" bind:group={certificateType} value="dismissal" />
                    <span>해촉증명서</span>
                  </label>
                </div>
              </div>

              <div class="form-actions">
                <button type="submit" class="btn-primary" disabled={isLoading}>
                  {isLoading ? '처리 중...' : '신청하기'}
                </button>
              </div>
            </form>
          {/if}
        </div>

      {:else if activeTab === 'history'}
        <!-- 신청 내역 -->
        <div class="history-section">
          {#if !user}
            <div class="login-notice">
              <p>신청 내역을 확인하려면 로그인이 필요합니다.</p>
              <button class="btn-primary" on:click={() => setPage('login')}>로그인</button>
            </div>
          {:else if isLoading}
            <div class="loading">불러오는 중...</div>
          {:else if certificates.length === 0}
            <div class="empty-state">신청 내역이 없습니다.</div>
          {:else}
            <div class="certificate-list">
              <table>
                <thead>
                  <tr>
                    <th>신청일</th>
                    <th>종류</th>
                    <th>재직기간</th>
                    <th>상태</th>
                    <th>관리</th>
                  </tr>
                </thead>
                <tbody>
                  {#each certificates as cert}
                    <tr>
                      <td>{formatDate(cert.created_at)}</td>
                      <td>{typeText[cert.type]}</td>
                      <td>{formatDate(cert.start_date)} ~ {formatDate(cert.end_date)}</td>
                      <td>
                        <span class="status-badge {statusClass[cert.status]}">
                          {statusText[cert.status]}
                        </span>
                      </td>
                      <td>
                        <button class="btn-small" on:click={() => openDetailModal(cert)}>상세</button>
                        {#if cert.status === 0}
                          <button class="btn-small btn-danger" on:click={() => handleDelete(cert.id)}>삭제</button>
                        {/if}
                        {#if cert.status === 1 || cert.status === 3}
                          {#if isDownloadExpired(cert)}
                            <span class="expired-notice">기간 만료</span>
                          {:else}
                            <button class="btn-small btn-success" on:click={() => downloadPDF(cert.id)}>PDF 다운로드</button>
                          {/if}
                          <!-- 일반 사용자는 DOCX 다운로드 불가 -->
                        {/if}
                      </td>
                    </tr>
                  {/each}
                </tbody>
              </table>
            </div>
          {/if}
        </div>

      {:else if activeTab === 'admin' && isAdmin}
        <!-- 관리자 페이지 -->
        <div class="admin-section">
          <div class="admin-filters">
            <select bind:value={adminFilter.type} on:change={loadAdminCertificates}>
              <option value="">전체 종류</option>
              <option value="appointment">위촉증명서</option>
              <option value="dismissal">해촉증명서</option>
            </select>
            <select bind:value={adminFilter.status} on:change={loadAdminCertificates}>
              <option value="">전체 상태</option>
              <option value="0">신청</option>
              <option value="1">승인</option>
              <option value="2">반려</option>
              <option value="3">재발급</option>
            </select>
            <input
              type="text"
              placeholder="이름/이메일 검색"
              bind:value={adminFilter.search}
              on:keyup={(e) => e.key === 'Enter' && loadAdminCertificates()}
            />
            <button class="btn-secondary" on:click={loadAdminCertificates}>검색</button>
          </div>

          {#if isLoading}
            <div class="loading">불러오는 중...</div>
          {:else if adminCertificates.length === 0}
            <div class="empty-state">신청 내역이 없습니다.</div>
          {:else}
            <div class="certificate-list">
              <table>
                <thead>
                  <tr>
                    <th>신청일</th>
                    <th>신청자</th>
                    <th>종류</th>
                    <th>성명</th>
                    <th>재직기간</th>
                    <th>상태</th>
                    <th>관리</th>
                  </tr>
                </thead>
                <tbody>
                  {#each adminCertificates as cert}
                    <tr>
                      <td>{formatDate(cert.created_at)}</td>
                      <td>{cert.user_email}</td>
                      <td>{typeText[cert.type]}</td>
                      <td>{cert.name}</td>
                      <td>{formatDate(cert.start_date)} ~ {formatDate(cert.end_date)}</td>
                      <td>
                        <span class="status-badge {statusClass[cert.status]}">
                          {statusText[cert.status]}
                        </span>
                      </td>
                      <td>
                        <button class="btn-small" on:click={() => openDetailModal(cert)}>
                          {cert.status === 0 ? '처리' : '상세'}
                        </button>
                      </td>
                    </tr>
                  {/each}
                </tbody>
              </table>
            </div>
          {/if}
        </div>
      {/if}
    </div>
  </div>

  <!-- 상세 모달 -->
  {#if showDetailModal && selectedCertificate}
    <div class="modal-overlay" on:click={closeDetailModal}>
      <div class="modal-content" on:click|stopPropagation>
        <div class="modal-header">
          <h3>{typeText[selectedCertificate.type]} 상세</h3>
          <button class="modal-close" on:click={closeDetailModal}>&times;</button>
        </div>
        <div class="modal-body">
          {#if isEditMode && isAdmin}
            <!-- 수정 모드 -->
            <div class="edit-form">
              <div class="form-row">
                <div class="form-group">
                  <label for="edit-name">성명 <span class="required">*</span></label>
                  <input type="text" id="edit-name" bind:value={editFormData.name} required />
                </div>
                <div class="form-group">
                  <label for="edit-residentNumber">주민등록번호</label>
                  <input type="text" id="edit-residentNumber" bind:value={editFormData.residentNumber} placeholder="000000-0000000" />
                </div>
              </div>

              <div class="form-group">
                <label for="edit-address">주소</label>
                <input type="text" id="edit-address" bind:value={editFormData.address} />
              </div>

              <div class="form-row">
                <div class="form-group">
                  <label for="edit-department">부서</label>
                  <input type="text" id="edit-department" bind:value={editFormData.department} />
                </div>
                <div class="form-group">
                  <label for="edit-position">직위</label>
                  <input type="text" id="edit-position" bind:value={editFormData.position} />
                </div>
              </div>

              <div class="form-row">
                <div class="form-group">
                  <label for="edit-startDate">재직기간 시작일</label>
                  <input type="date" id="edit-startDate" bind:value={editFormData.startDate} />
                </div>
                <div class="form-group">
                  <label for="edit-endDate">재직기간 종료일</label>
                  <input type="date" id="edit-endDate" bind:value={editFormData.endDate} />
                </div>
              </div>

              <div class="form-group">
                <label for="edit-purpose">발급용도</label>
                <input type="text" id="edit-purpose" bind:value={editFormData.purpose} />
              </div>

              <div class="form-group">
                <label for="edit-contact">연락처</label>
                <input type="text" id="edit-contact" bind:value={editFormData.contact} placeholder="010-0000-0000" />
              </div>

              <div class="edit-actions">
                <button class="btn-secondary" on:click={toggleEditMode} disabled={isUpdating}>취소</button>
                <button class="btn-primary" on:click={handleAdminUpdate} disabled={isUpdating}>
                  {isUpdating ? '저장 중...' : '저장'}
                </button>
              </div>
            </div>
          {:else}
            <!-- 보기 모드 -->
            <div class="detail-grid">
              <div class="detail-item">
                <label>성명</label>
                <span>{selectedCertificate.name}</span>
              </div>
              <div class="detail-item">
                <label>주민등록번호</label>
                <span>{selectedCertificate.resident_number || '-'}</span>
              </div>
              <div class="detail-item full-width">
                <label>주소</label>
                <span>{selectedCertificate.address || '-'}</span>
              </div>
              <div class="detail-item">
                <label>부서</label>
                <span>{selectedCertificate.department || '-'}</span>
              </div>
              <div class="detail-item">
                <label>직위</label>
                <span>{selectedCertificate.position || '-'}</span>
              </div>
              <div class="detail-item">
                <label>재직기간</label>
                <span>{formatDate(selectedCertificate.start_date)} ~ {formatDate(selectedCertificate.end_date)}</span>
              </div>
              <div class="detail-item">
                <label>발급용도</label>
                <span>{selectedCertificate.purpose || '-'}</span>
              </div>
              <div class="detail-item">
                <label>연락처</label>
                <span>{selectedCertificate.contact || '-'}</span>
              </div>
              <div class="detail-item">
                <label>상태</label>
                <span class="status-badge {statusClass[selectedCertificate.status]}">
                  {statusText[selectedCertificate.status]}
                </span>
              </div>
              {#if selectedCertificate.approved_at}
                <div class="detail-item">
                  <label>처리일시</label>
                  <span>{formatDateTime(selectedCertificate.approved_at)}</span>
                </div>
              {/if}
              {#if selectedCertificate.admin_comment}
                <div class="detail-item full-width">
                  <label>관리자 코멘트</label>
                  <div class="comment-section">
                    <span class="comment-text">{selectedCertificate.admin_comment}</span>
                    {#if isAdmin && (selectedCertificate.status === 1 || selectedCertificate.status === 2 || selectedCertificate.status === 3)}
                      <button class="btn-small btn-info" on:click={() => openEditCommentModal()}>
                        코멘트 수정
                      </button>
                    {/if}
                  </div>
                </div>

                {#if selectedCertificate.comment_updated_at}
                  <div class="detail-item">
                    <label>코멘트 수정 정보</label>
                    <div class="comment-update-info">
                      <span class="update-time">수정 시간: {formatDateTime(selectedCertificate.comment_updated_at)}</span>
                      {#if selectedCertificate.comment_updated_by_name}
                        <span class="update-user">수정자: {selectedCertificate.comment_updated_by_name}</span>
                      {/if}
                    </div>
                  </div>
                {/if}
              {/if}
            </div>
          {/if}

          {#if isAdmin}
            {#if selectedCertificate.status === 0}
              <div class="admin-action">
                <div class="form-group">
                  <label for="adminComment">관리자 코멘트</label>
                  <textarea id="adminComment" bind:value={adminComment} rows="3"></textarea>
                </div>
                <div class="action-buttons">
                  <button
                    class="btn-success"
                    on:click={() => handleStatusChange(1)}
                    disabled={isLoading}
                  >
                    승인
                  </button>
                  <button
                    class="btn-danger"
                    on:click={() => handleStatusChange(2)}
                    disabled={isLoading}
                  >
                    반려
                  </button>
                  <button
                    class="btn-info"
                    on:click={toggleEditMode}
                    disabled={isUpdating}
                  >
                    정보 수정
                  </button>
                </div>
              </div>
            {:else if selectedCertificate.status === 1 || selectedCertificate.status === 3}
              <!-- 승인 또는 재발급 상태인 경우 재발급 -->
              <div class="admin-action">
                <div class="form-group">
                  <label for="adminComment">관리자 코멘트 (재발급 사유)</label>
                  <textarea id="adminComment" bind:value={adminComment} rows="3" placeholder="재발급 사유를 입력해주세요"></textarea>
                </div>
                <div class="action-buttons">
                  <button
                    class="btn-warning"
                    on:click={() => handleStatusChange(3)}
                    disabled={isLoading}
                  >
                    재발급
                  </button>
                  <button
                    class="btn-info"
                    on:click={toggleEditMode}
                    disabled={isUpdating}
                  >
                    정보 수정
                  </button>
                </div>
              </div>
            {:else}
              <!-- 반려 상태인 경우 정보 수정만 -->
              <div class="admin-action">
                <div class="action-buttons">
                  <button
                    class="btn-info"
                    on:click={toggleEditMode}
                    disabled={isUpdating}
                  >
                    정보 수정
                  </button>
                </div>
              </div>
            {/if}
          {/if}

          <!-- 관리자용 PDF 업로드 섹션 (만료되지 않은 경우에만) -->
          {#if isAdmin && !isDownloadExpired(selectedCertificate)}
            <div class="pdf-upload-section">
              <h4>PDF 파일 관리</h4>
              {#if selectedCertificate.pdf_file_id}
                <div class="pdf-status">
                  <span class="pdf-uploaded">✓ PDF 파일이 업로드되어 있습니다.</span>
                  <button class="btn-small btn-danger" on:click={handlePdfDelete}>PDF 삭제</button>
                </div>
              {:else}
                <div class="pdf-upload-form">
                  <input
                    type="file"
                    accept=".pdf"
                    bind:this={pdfFileInput}
                    on:change={handlePdfFileSelect}
                  />
                  {#if selectedPdfFile}
                    <div class="selected-file">
                      <span>{selectedPdfFile.name}</span>
                      <button
                        class="btn-primary btn-small"
                        on:click={handlePdfUpload}
                        disabled={isUploading}
                      >
                        {isUploading ? '업로드 중...' : 'PDF 업로드'}
                      </button>
                    </div>
                  {/if}
                </div>
              {/if}
            </div>
          {/if}

          {#if selectedCertificate.status === 1 || selectedCertificate.status === 3}
            <div class="download-section">
              {#if isDownloadExpired(selectedCertificate)}
                <div class="expired-notice-box">
                  <span class="expired-icon">⚠️</span>
                  <span>다운로드 기간(2주) 만료로 재다운로드가 불가능합니다. 재신청 바랍니다.</span>
                </div>
              {:else}
                <button class="btn-primary btn-large" on:click={() => downloadPDF(selectedCertificate.id)}>
                  PDF 다운로드
                </button>
                {#if isAdmin}
                  <button class="btn-info btn-large" on:click={() => downloadDOCX(selectedCertificate.id)}>
                    DOCX 다운로드
                  </button>
                {/if}
              {/if}
            </div>
          {/if}
        </div>
      </div>
    </div>
  {/if}

  <!-- 코멘트 수정 모달 -->
  {#if showEditCommentModal && selectedCertificate}
    <div class="modal-overlay" on:click={closeEditCommentModal}>
      <div class="modal-content comment-modal" on:click|stopPropagation>
        <div class="modal-header">
          <h3>관리자 코멘트 수정</h3>
          <button class="modal-close" on:click={closeEditCommentModal}>&times;</button>
        </div>
        <div class="modal-body">
          <div class="certificate-info">
            <h4>증명서 정보</h4>
            <div class="info-grid">
              <div class="info-item">
                <label>신청자</label>
                <span>{selectedCertificate.user_name}</span>
              </div>
              <div class="info-item">
                <label>증명서 종류</label>
                <span>{typeText[selectedCertificate.type]}</span>
              </div>
              <div class="info-item">
                <label>성명</label>
                <span>{selectedCertificate.name}</span>
              </div>
              <div class="info-item">
                <label>현재 상태</label>
                <span class="status-badge {statusClass[selectedCertificate.status]}">
                  {statusText[selectedCertificate.status]}
                </span>
              </div>
            </div>
          </div>

          <div class="comment-edit-section">
            <div class="form-group">
              <label for="editingComment">관리자 코멘트 수정 <span class="required">*</span></label>
              <textarea
                id="editingComment"
                bind:value={editingComment}
                rows="5"
                placeholder="수정할 코멘트를 입력해주세요"
                disabled={isCommentUpdating}
              ></textarea>
            </div>

            <div class="comment-history">
              <h5>기존 코멘트</h5>
              <div class="existing-comment">
                <div class="comment-text">{selectedCertificate.admin_comment || '없음'}</div>
                {#if selectedCertificate.comment_updated_at}
                  <div class="comment-meta">
                    <small>
                      <strong>최종 수정:</strong>
                      {formatDateTime(selectedCertificate.comment_updated_at)}
                      {#if selectedCertificate.comment_updated_by_name}
                        (수정자: {selectedCertificate.comment_updated_by_name})
                      {/if}
                    </small>
                  </div>
                {/if}
              </div>
            </div>
          </div>
        </div>

        <div class="modal-footer">
          <button
            class="btn-secondary"
            on:click={closeEditCommentModal}
            disabled={isCommentUpdating}
          >
            취소
          </button>
          <button
            class="btn-primary"
            on:click={handleCommentUpdate}
            disabled={isCommentUpdating || !editingComment.trim()}
          >
            {isCommentUpdating ? '수정 중...' : '코멘트 수정'}
          </button>
        </div>
      </div>
    </div>
  {/if}

  <!-- 신청 완료 모달 -->
  {#if showSuccessModal}
    <div class="modal-overlay" on:click={() => { showSuccessModal = false; activeTab = 'history'; }}>
      <div class="modal-content success-modal" on:click|stopPropagation>
        <div class="success-icon">✓</div>
        <h3>신청 완료</h3>
        <p>증명서 신청이 완료되었습니다.</p>
        <p class="sub-text">관리자 승인 후 PDF 다운로드가 가능합니다.</p>
        <button class="btn-primary" on:click={() => { showSuccessModal = false; activeTab = 'history'; }}>확인</button>
      </div>
    </div>
  {/if}

  <footer>
    <div class="company-info">
      <h3>주식회사 비전와이드</h3>
      <p>대표자명 : 최병영 | 사업자등록번호 : 134-87-40607 | 유료직업소개사업등록번호(국내): 제2026-3900373-14-5-00003호</p>
      <p>경기도 광명시 오리로854번길 12, 3층 304호(철산동, 세인트빌딩)</p>
      <p>TEL : 02-565-5747 | FAX : 02-6499-5747 | E-mail : admin@visionwide.kr</p>
      <p>©VISIONWIDE. ALL RIGHTS RESERVED.</p>
    </div>
  </footer>
</div>
